import { useEffect, useState } from 'react';
import { VideoEffectStore } from '../store/videoEffectModel';
import { BackgroundImage, BlurEffect, VideoEffectPreferences } from '../videoEffectTypes';

const imageValidationCache = new Map<string, Promise<boolean>>();

function validateImageResource(url: string): Promise<boolean> {
  // istanbul ignore next
  if (!url) return Promise.resolve(false);
  const cached = imageValidationCache.get(url);
  if (cached) return cached;

  const request = new Promise<boolean>((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => {
      console.warn(`Background asset unreachable: ${url}`);
      imageValidationCache.delete(url);
      resolve(false);
    };
    img.src = url;
  });

  imageValidationCache.set(url, request);
  return request;
}

export interface UsePreferenceHydrationParams {
  applyBackground: (image: BackgroundImage | null) => Promise<void>;
  applyBlur: (effect: BlurEffect) => Promise<void>;
  removeAllEffects: () => Promise<void>;
}

export function usePreferenceHydration({ applyBackground, applyBlur, removeAllEffects }: UsePreferenceHydrationParams) {
  const preferences = VideoEffectStore.useStoreState((actions) => actions.preferences);
  const isFetchingPreferences = VideoEffectStore.useStoreState((actions) => actions.isFetchingPreferences);
  const videoTrack = VideoEffectStore.useStoreState((state) => state.localVideoTrack);

  const [hydratedPreferences, setHydratedPreferences] = useState<VideoEffectPreferences | null>(null);

  useEffect(() => {
    if (isFetchingPreferences || !preferences || !videoTrack) return;
    if (hydratedPreferences === preferences) return;

    const { participantPreference } = preferences;

    async function hydrate() {
      const savedImage = participantPreference?.backgroundImage;

      if (savedImage) {
        const targetUrl = savedImage?.url ?? '';
        const isAccessible = savedImage.preExisting ? Boolean(targetUrl) : await validateImageResource(targetUrl);

        if (isAccessible) {
          const bg: BackgroundImage = {
            id: savedImage.id,
            label: '',
            url: targetUrl,
            filePath: savedImage?.filePath ?? /* istanbul ignore next */ '',
            preExisting: savedImage.preExisting ?? false,
          };
          await applyBackground(bg);
        } else {
          console.warn(`Saved background asset "${savedImage.id}" is unresolvable. Clearing effects.`);
          await removeAllEffects();
        }
      } else {
        const savedBlur = participantPreference?.blurEffect;
        if (savedBlur && savedBlur !== 'none') {
          await applyBlur(savedBlur as BlurEffect);
        }
      }

      setHydratedPreferences(preferences);
    }

    hydrate().catch((err) => {
      console.error('Preference hydration failed:', err);
    });
  }, [isFetchingPreferences, preferences, videoTrack, hydratedPreferences, applyBlur, applyBackground, removeAllEffects]);

  return hydratedPreferences;
}
